import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { MaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { useColumnSettings } from "../Utils/useColumnSettings";
import { Loader } from "../Utils/Loader";

import { getItems } from "../../slices/Item/GetItem/thunk";

import InventoryPOForm from "./InventoryPOForm";
import InventorySOForm from "./InventorySOForm";
import TransferForm from "../Transfer/TransferForm";
import AdjustmentForm from "../Adjustment/AdjustmentForm";

import InventoryHistory from "./InventoryHistory";

function InventoryListings() {
  const dispatch = useDispatch();

  const { items, loading } = useSelector((state) => state.GetItems || {});

  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [allItemsData, setAllItemsData] = useState([]);

  const [selectedItem, setSelectedItem] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState([]);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  const toggleModal = () => setModalOpen((p) => !p);

  const openForm = (type, row) => {
    setModalType(type);
    setSelectedRow(row);
    setModalOpen(true);
  };

  useEffect(() => {
    dispatch(getItems(pagination.pageSize, pagination.pageIndex + 1));
  }, [dispatch, pagination.pageSize, pagination.pageIndex]);

  useEffect(() => {
    if (!items?.items) return;

    const formattedData = items.items.map((item, index) => ({
      ...item,
      id: index + 1,
      category: item.categorypath?.[0] || "-",
    }));

    setAllItemsData((prev) => {
      const merged = [...prev, ...formattedData];

      return merged.filter(
        (item, index, self) =>
          index === self.findIndex((x) => x.itemid === item.itemid),
      );
    });
  }, [items]);

  const tableData = useMemo(() => {
    if (!items?.items) return [];

    return items.items.map((item, index) => ({
      ...item,
      id: index + 1,
      category: item.categorypath?.[0] || "-",
    }));
  }, [items]);

  const filteredData = useMemo(() => {
    if (!appliedSearch) return tableData;

    const search = appliedSearch.toLowerCase().trim();

    return allItemsData.filter(
      (item) =>
        item.itemname?.toLowerCase().includes(search) ||
        item.sku?.toLowerCase().includes(search) ||
        item.category?.toLowerCase().includes(search),
    );
  }, [tableData, appliedSearch, allItemsData]);

  const columns = [
    { accessorKey: "sku", header: "SKU" },
    { accessorKey: "itemname", header: "Product Name" },
    { accessorKey: "category", header: "Category" },
    { accessorKey: "itemcount", header: "Available Quantity" },
  ];

  const handleSearch = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));

    setAppliedSearch(searchText.trim());
  };

  const handleRowClick = (row) => {
    setSelectedItem(row.original);
    setShowHistory(true);
  };

  const {
    settingsModalOpen,
    viewColumns,
    toggleSettingsModal,
    SettingsSidebar,
    CustomViewModal,
  } = useColumnSettings("inventory-listings", columns);

  return (
    <>
      <Row>
        {/* LEFT TABLE */}
        <Col lg={showHistory ? 8 : 12} className="px-4">
          <div className="mb-3">
            <h4 className="mb-0 fw-semibold">Inventory List</h4>
          </div>

          {/* SEARCH */}
          <div className="d-flex justify-content-between align-items-center mb-2 gap-3">
            <div className="d-flex align-items-center w-100 gap-2">
              <Input
                type="text"
                placeholder="Search by SKU and Product name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{
                  maxWidth: "500px",
                  height: "36px",
                  borderRadius: "4px",
                }}
              />

              <Button
                className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap mx-1"
                onClick={handleSearch}
              >
                <i className="ri-search-line label-icon align-middle fs-16 me-1 fw-bold"></i>
                <span className="ps-2 ms-2 me-1 fw-bold">Search</span>
              </Button>
            </div>

            {(columnFilters.length > 0 || showColumnFilters) && (
              <Button
                color="secondary"
                outline
                size="sm"
                className="text-nowrap"
                onClick={() => {
                  setColumnFilters([]);
                  setShowColumnFilters(false);
                }}
              >
                <i className="ri-filter-off-line me-1"></i>
                Clear Filters
              </Button>
            )}
          </div>

          {/* TABLE */}
          <div
            className="border table-responsive"
            style={{ borderRadius: "4px" }}
          >
            {loading && <Loader />}

            <MaterialReactTable
              columns={
                viewColumns?.length
                  ? columns.filter((col) =>
                      viewColumns.includes(col.accessorKey || col.header),
                    )
                  : columns
              }
              data={filteredData}
              enableTopToolbar={false}
              enableColumnActions
              enableColumnOrdering={false}
              manualPagination
              rowCount={items?.totalcount || 0}
               onPaginationChange={setPagination}
              state={{ pagination, columnFilters, showColumnFilters }}
              onColumnFiltersChange={(filters) => {
                setColumnFilters(filters);
                if (filters.length === 0) setShowColumnFilters(false);
              }}
              onShowColumnFiltersChange={setShowColumnFilters}
              enableRowActions
              positionActionsColumn="first"
              displayColumnDefOptions={{
                "mrt-row-actions": {
                  header: "",
                  size: 20,
                  minSize: 20,
                  maxSize: 20,
                },
              }}
              muiTableBodyCellProps={({ column }) => ({
                sx:
                  column.id === "mrt-row-actions"
                    ? {
                        padding: "0 0px",
                      }
                    : {},
              })}
              muiTableBodyRowProps={({ row }) => ({
                onClick: () => handleRowClick(row),
                sx: {
                  cursor: "pointer",
                  backgroundColor:
                    selectedItem?.itemid === row.original.itemid
                      ? "#FFF7ED"
                      : "inherit",
                },
              })}
              renderRowActionMenuItems={({ row, closeMenu }) => [
                <MenuItem
                  key="po"
                  onClick={() => {
                    closeMenu();
                    openForm("PO", row.original);
                  }}
                >
                  <i className="ri-shopping-cart-2-line me-2" />
                  <span className="small">Purchase Order</span>
                </MenuItem>,

                <MenuItem
                  key="so"
                  onClick={() => {
                    closeMenu();
                    openForm("SO", row.original);
                  }}
                >
                  <i className="ri-price-tag-3-line me-2" />
                  <span className="small">Sales Order</span>
                </MenuItem>,

                <MenuItem
                  key="transfer"
                  onClick={() => {
                    closeMenu();
                    openForm("TRANSFER", {
                      ...row.original,
                      disabledItem: true,
                    });
                  }}
                >
                  <i className="ri-arrow-left-right-line me-2" />
                  <span className="small">Stock Transfer</span>
                </MenuItem>,

                <MenuItem
                  key="adjust"
                  onClick={() => {
                    closeMenu();
                    openForm("ADJUSTMENT", {
                      ...row.original,
                      disabledItem: true,
                    });
                  }}
                >
                  <i className="ri-drag-move-line me-2" />
                  <span className="small">Stock Adjustment</span>
                </MenuItem>,
              ]}
            />
          </div>
        </Col>

        {/* RIGHT HISTORY PANEL */}
        {showHistory && (
          <Col lg={4} className="ps-4 pe-4 pe-lg-3 ps-lg-0 mt-md-3">
            <InventoryHistory
              selectedItem={selectedItem}
              onClose={() => {
                setShowHistory(false);
                setSelectedItem(null);
              }}
            />
          </Col>
        )}

        {CustomViewModal()}
      </Row>

      {/* MODAL */}
      <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
        <ModalHeader
          toggle={toggleModal}
          style={{ borderBottom: "1px solid #FD7545", padding: "16px 20px" }}
        >
          <span
            style={{ color: "#FD7545", fontWeight: "600", fontSize: "18px" }}
          >
            {modalType === "PO" && "Purchase Order"}
            {modalType === "SO" && "Sales Order"}
            {modalType === "TRANSFER" && "Stock Transfer"}
            {modalType === "ADJUSTMENT" && "Stock Adjustment"}
          </span>
        </ModalHeader>

        <ModalBody>
          {modalType === "PO" && (
            <InventoryPOForm
              closeModal={toggleModal}
              selectedRow={selectedRow}
            />
          )}

          {modalType === "SO" && (
            <InventorySOForm
              closeModal={toggleModal}
              selectedRow={selectedRow}
            />
          )}

          {modalType === "TRANSFER" && (
            <TransferForm closeModal={toggleModal} selectedRow={selectedRow} />
          )}

          {modalType === "ADJUSTMENT" && (
            <AdjustmentForm
              closeModal={toggleModal}
              selectedRow={selectedRow}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

export default InventoryListings;
